/**
 * <s-product-gallery> — follows the variant picker on the product page.
 *
 * When a shopper picks an option (for example a color), the gallery moves that
 * variant's photo, plus the photo right after it (usually the back view), to the
 * top as the large lead image. Every other photo keeps its original order.
 * On mobile the swipe row scrolls back to the start; on desktop the gallery
 * scrolls into view if its top is above the viewport.
 *
 * Required markup:
 *   script[type="application/json"][data-variant-media] — { "<variant id>": "<media id>" }
 *   [data-gallery-track]                                — the element whose children are gallery items
 *   [data-gallery-item][data-media-id]                  — one per photo, in catalog order
 *   data-lead-sizes / data-pair-sizes on this element   — responsive `sizes` for the lead and paired slots
 *
 * The picker inputs live outside this element (and are replaced by partial rendering),
 * so selection is observed with a document-level delegated `change` listener.
 */
const LEAD_CLASS = 'md:col-span-2';

class SProductGallery extends HTMLElement {
  #ac;
  #variantMedia = {};
  #variantMediaIds = new Set();
  #originalOrder = [];
  #track = null;

  connectedCallback() {
    this.#ac = new AbortController();
    this.#track = this.querySelector('[data-gallery-track]');
    if (!this.#track) return;

    try {
      const json = this.querySelector('script[data-variant-media]')?.textContent || '{}';
      this.#variantMedia = JSON.parse(json);
    } catch {
      this.#variantMedia = {};
    }
    this.#variantMediaIds = new Set(Object.values(this.#variantMedia).map(String));
    this.#originalOrder = Array.from(this.#track.children);

    document.addEventListener('change', this.#handleChange, { signal: this.#ac.signal });

    // Deep links (?variant=) open on that variant's photos.
    const variantId = new URL(location.href).searchParams.get('variant');
    if (variantId) this.#showVariant(variantId, false);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #handleChange = event => {
    const target = event.target;
    if (!(target instanceof HTMLElement) || !target.closest('s-variant-picker')) return;

    let variantId = target.dataset.variantId;
    if (target instanceof HTMLSelectElement) {
      variantId = target.selectedOptions[0]?.dataset.variantId;
    }
    if (variantId) this.#showVariant(variantId, true);
  };

  #showVariant(variantId, shouldScroll) {
    const mediaId = this.#variantMedia[String(variantId)];
    if (!mediaId) return;

    const front = this.#originalOrder.find(item => item.dataset.mediaId === String(mediaId));
    if (!front) return;

    // Pair the variant photo with the next catalog photo unless that one belongs to another variant.
    const frontIndex = this.#originalOrder.indexOf(front);
    const next = this.#originalOrder[frontIndex + 1];
    const leadItems = [front];
    if (next && next.dataset.mediaId && !this.#variantMediaIds.has(next.dataset.mediaId)) {
      leadItems.push(next);
    }

    const rest = this.#originalOrder.filter(item => !leadItems.includes(item));
    const ordered = [...leadItems, ...rest];

    if (this.#track.firstElementChild !== front) {
      for (const item of ordered) this.#track.append(item);
    }
    this.#applyLayout();

    if (shouldScroll) this.#scrollToStart();
  }

  #applyLayout() {
    const leadSizes = this.dataset.leadSizes;
    const pairSizes = this.dataset.pairSizes;

    Array.from(this.#track.children).forEach((item, index) => {
      const isLead = index === 0;
      item.classList.toggle(LEAD_CLASS, isLead);
      const img = item.querySelector('img');
      if (!img) return;
      if (isLead) {
        if (leadSizes) img.sizes = leadSizes;
        img.loading = 'eager';
      } else if (pairSizes && img.sizes !== 'auto') {
        img.sizes = pairSizes;
      }
    });
  }

  #scrollToStart() {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const behavior = reduceMotion ? 'auto' : 'smooth';

    this.#track.scrollTo({ left: 0, behavior });

    const top = this.getBoundingClientRect().top;
    if (top < 0) this.scrollIntoView({ block: 'start', behavior });
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
